/**
 * Emote manifest — shared between the avatar (playback), the emote wheel (UI),
 * and multiplayer sync. Each emote maps to an animation-only GLB in
 * /public/animations/emotes/. loop:false emotes play once and return to idle;
 * loop:true emotes (dances) loop until the player moves.
 *
 * All 20 files are stored with the application. Playback does not call Meshy.
 * Hug and High Five are individual gestures, not synchronized paired interactions.
 */



import tripoLibrary from '../avatar/tripoLibrary.json';

export interface EmoteDef {
  name: string;
  label: string;
  emoji: string;
  url: string;
  loop: boolean;
  /** Optional image icon (GathrXR icon set) shown instead of the emoji. */
  icon?: string;
  /** Tripo library move (see LIBRARY_EMOTES): loaded the first time it plays. */
  category?: string;
}

export const EMOTES: EmoteDef[] = [
  { name: 'wave', label: 'Wave', emoji: '👋', url: '/animations/emotes/wave.glb', loop: false },
  { name: 'cheer', label: 'Cheer', emoji: '🙌', url: '/animations/emotes/cheer.glb', loop: false },
  { name: 'bow', label: 'Bow', emoji: '🙇', url: '/animations/emotes/bow.glb', loop: false },
  { name: 'heart', label: 'Heart', emoji: '❤️', url: '/animations/emotes/heart.glb', loop: false },
  { name: 'shrug', label: 'Shrug', emoji: '🤷', url: '/animations/emotes/shrug.glb', loop: false },
  { name: 'fistpump', label: 'Fist Pump', emoji: '✊', url: '/animations/emotes/fistpump.glb', loop: false },
  { name: 'dance_funny', label: 'Funny Dance', emoji: '🕺', url: '/animations/emotes/dance_funny.glb', loop: true },
  { name: 'dance_gangnam', label: 'Gangnam', emoji: '🎵', url: '/animations/emotes/dance_gangnam.glb', loop: true },
  { name: 'dance_hiphop', label: 'Hip Hop', emoji: '💃', url: '/animations/emotes/dance_hiphop.glb', loop: true },
  { name: 'dance_shake', label: 'Shake It', emoji: '✨', url: '/animations/emotes/dance_shake.glb', loop: true },
  { name: 'twerk', label: 'Twerk', emoji: '🍑', url: '/animations/emotes/twerk.glb?v=1', loop: true },
  // Sit is a looping "pose" emote — holds until the player moves (stands up).
  // Triggered from the wheel (sit-in-place) and by clicking a marked seat.
  { name: 'sit', label: 'Sit', emoji: '🪑', url: '/animations/emotes/sit.glb?v=9', loop: true, icon: '/icons/ui/sit-chair.png' },
  { name: 'sit_floor', label: 'Floor Sit', emoji: '🧘', url: '/animations/emotes/sit_floor.glb?v=1', loop: true, icon: '/icons/ui/sit-floor.png' },
  { name: 'sit_squat', label: 'Squat', emoji: '🧎', url: '/animations/emotes/sit_squat.glb?v=1', loop: true, icon: '/icons/ui/sit-squat.png' },
  { name: 'dance_salsa', label: 'Salsa', emoji: '💃', url: '/animations/emotes/dance_salsa.glb', loop: true },
  { name: 'dance_robot', label: 'Robot Dance', emoji: '🤖', url: '/animations/emotes/dance_robot.glb', loop: true },
  { name: 'clap', label: 'Clap', emoji: '👏', url: '/animations/emotes/clap.glb', loop: false },
  { name: 'laugh', label: 'Laugh', emoji: '😄', url: '/animations/emotes/laugh.glb', loop: false },
  { name: 'point', label: 'Point', emoji: '👉', url: '/animations/emotes/point.glb', loop: false },
  { name: 'blow_kiss', label: 'Blow a Kiss', emoji: '😘', url: '/animations/emotes/blow_kiss.glb', loop: false },
  { name: 'high_five', label: 'High Five', emoji: '✋', url: '/animations/emotes/high_five.glb', loop: false },
  { name: 'hug', label: 'Hug', emoji: '🤗', url: '/animations/emotes/hug.glb', loop: false },
];

// ---- Tripo animation library ----
// Every Tripo preset (~100 moves), generated ONCE on one library avatar's rig
// (scripts/tripo-build-library.mjs) and retargeted at load time onto whoever
// plays it. Names are `tl_<preset>` so they never collide with the wheel's.
export const LIBRARY_SOURCE_MODEL: string = tripoLibrary.sourceSkeleton;
const LIBRARY_LOOPS = /^(dance_|freaky|sing_|idle|walk|run|swim|surf|climb|sit|standing_relax|wait|look_around|swagger|jump_rope|dribble|crossover_dribble|play_|make_a_call|fold_arms|cry|sob|depressed|afraid|scared|warm_up|press-up|cross_body_crunch|dig|chop|shovel|box_|flee_)/;
const LIBRARY_EMOJI: Record<string, string> = { basic: '🚶', dance: '💃', performance: '🎤', emotion: '💬', daily: '☕', sports: '⚽', combat: '🥊' };
const LABEL_FIX: Record<string, string> = { 'press-up': 'Push-Ups', wave_goodbye_01: 'Wave Goodbye', wave_goodbye_02: 'Wave Goodbye 2', make_a_call_01: 'Phone Call', make_a_call_02: 'Phone Call 2', freaky: 'Freaky Dance', heart_pose: 'Heart Pose', victory_celebration: 'Victory', cast_a_spell: 'Cast a Spell' };
const prettify = (n: string) => LABEL_FIX[n] || n.replace(/_0?(\d+)$/, ' $1').replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
export const LIBRARY_CATEGORIES = Object.keys(LIBRARY_EMOJI);
export const LIBRARY_EMOTES: EmoteDef[] = Object.entries(tripoLibrary.clips as Record<string, { category: string; url: string }>)
  .sort(([a, x], [b, y]) => LIBRARY_CATEGORIES.indexOf(x.category) - LIBRARY_CATEGORIES.indexOf(y.category) || a.localeCompare(b, undefined, { numeric: true }))
  .map(([preset, c]) => ({ name: `tl_${preset}`, label: prettify(preset), emoji: LIBRARY_EMOJI[c.category] || '✨', url: c.url, loop: LIBRARY_LOOPS.test(preset), category: c.category }));

/** Wheel emotes a Tripo-rigged avatar plays from the library instead of the shared Meshy-era clip. */
export const TRIPO_EQUIVALENT: Record<string, string> = {
  wave: 'tl_wave_goodbye_01', cheer: 'tl_cheer', bow: 'tl_bow', heart: 'tl_heart_pose', fistpump: 'tl_victory_celebration',
  clap: 'tl_clap', laugh: 'tl_laugh_01', hug: 'tl_hug', dance_hiphop: 'tl_dance_01', dance_funny: 'tl_dance_02',
};

export const EMOTE_MAP: Record<string, EmoteDef> = Object.fromEntries([...EMOTES, ...LIBRARY_EMOTES].map((e) => [e.name, e]));

/**
 * Sitting styles. Clicking a seat uses the player's chosen style; while seated
 * they can switch between them. `drop` is how far the avatar root sits below
 * the seat's top surface: the chair clip's hips ride ~0.83m above its root
 * (legs hang to the floor), while the floor and squat clips keep the hips only
 * ~0.11–0.14m above it (seat/feet on the surface), so their root sits on the
 * surface itself.
 */
export interface SitStyle {
  emote: string;
  label: string;
  emoji: string;
  icon: string;
  /** Chair: root this far below the seat surface (tuned by eye). */
  drop: number;
  /**
   * Floor/squat: hip height in this pose as a fraction of standing hip
   * height, measured on a seated avatar in-world (the rotation-only clip
   * folds the legs and drops the hips to ~75% / ~85% of standing). Placement
   * lowers the root so the hips land SEAT_CLEARANCE above the seat surface.
   */
  poseHip?: number;
}

/** Hips this far above the seat surface for floor/squat sits (butt on top). */
export const SEAT_CLEARANCE = 0.22;

/**
 * Chair sits sized to the avatar, for seats that opt in (marker userData
 * `seatFit: 'chair'`, e.g. game-table chairs). The sit clip is rotation-only:
 * the hips stay ~0.868 × standing hip height above the root (measured 0.866 /
 * 0.870 on two avatars), ~0.064 × forward of it, and the feet ~0.16 × above
 * it. The fixed 0.5 m `drop` left a 1.85 m avatar's hips 0.37 m over the
 * cushion. Fitted: hips a little ahead of the cushion centre, CHAIR_HIP_CLEARANCE above it, unless that sinks
 * the feet more than CHAIR_FEET_SINK into the floor.
 */
export const CHAIR_POSE_HIP = 0.868;
export const CHAIR_POSE_HIP_FWD = 0.064;
export const CHAIR_POSE_FOOT = 0.16;
export const CHAIR_HIP_CLEARANCE = 0.12;
export const CHAIR_FEET_SINK = 0;
/**
 * Fitted chairs: hips this far forward of the seat centre. The clip's thighs
 * slope down ~45°, so hips nearer the front let the legs clear the cushion's
 * front edge while the seat of the avatar still rests on the cushion.
 */
export const CHAIR_HIP_AHEAD = 0.1;

/**
 * Floor/squat sits perch this far toward the seat's front edge. The converted
 * clips angle the thighs downward (knees ~0.22m below the hips), so sitting
 * centred buried the thighs in thick cushions; nearer the edge the lower legs
 * hang over the front instead.
 */
export const FLOOR_PERCH = 0.45;

export const SIT_STYLES: SitStyle[] = [
  { emote: 'sit', label: 'Chair', emoji: '🪑', icon: '/icons/ui/sit-chair.png', drop: 0.5 },
  { emote: 'sit_floor', label: 'Floor', emoji: '🧘', icon: '/icons/ui/sit-floor.png', drop: 0.0, poseHip: 0.755 },
  { emote: 'sit_squat', label: 'Squat', emoji: '🧎', icon: '/icons/ui/sit-squat.png', drop: 0.0, poseHip: 0.85 },
];

export const SIT_EMOTES = new Set(SIT_STYLES.map((s) => s.emote));

const SIT_STYLE_KEY = 'gathrxr.sitStyle';

/** The player's preferred sitting style (remembered per browser). */
export function getSitStyle(): SitStyle {
  let saved: string | null = null;
  try { saved = localStorage.getItem(SIT_STYLE_KEY); } catch { /* storage blocked */ }
  return SIT_STYLES.find((s) => s.emote === saved) || SIT_STYLES[0];
}

export function setSitStyle(emote: string): void {
  try { localStorage.setItem(SIT_STYLE_KEY, emote); } catch { /* storage blocked */ }
}

export function sitStyleFor(emote: string | null | undefined): SitStyle | undefined {
  return SIT_STYLES.find((s) => s.emote === emote);
}
